/*
 * Copyright 2026 Sony Corporation
 */

import { Color3 } from '@babylonjs/core/Maths/math.color';
import { Matrix, Quaternion, Vector3 } from '@babylonjs/core/Maths/math.vector';
import { MeshBuilder } from '@babylonjs/core/Meshes/meshBuilder';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import { VertexBuffer } from '@babylonjs/core/Buffers/buffer';
import type { Mesh } from '@babylonjs/core/Meshes/mesh';
import type { Scene } from '@babylonjs/core/scene';

import type { Sample } from '../coverage/coverage';
import type { Rgb } from '../coverage/colours';
import { subjectParts, type Part, type Point, type SubjectPlacement } from '../coverage/subject';

export interface CoverageSubject {
  readonly parts: readonly Part[];
  /** Every vertex of every part, in world space: what coverage is measured at. */
  readonly samples: readonly Sample[];
  /** Colours each sample; the mesh interpolates between them. */
  paint: (colourOf: (sample: number) => Rgb) => void;
  dispose: () => void;
}

/** Coarse on purpose: every vertex is tested against every camera. */
const TESSELLATION = 14;
const CAP_SUBDIVISIONS = 4;

/**
 * The coverage subject in the scene: the capsules of `subjectParts`, drawn.
 *
 * Editor-owned like the rigs, so it survives a scene switch, and not pickable,
 * so a click through it still selects the rig behind. On the scene layer, so a
 * camera preview sees the subject it is being judged on.
 */
export const createCoverageSubject = (scene: Scene, placement: SubjectPlacement): CoverageSubject => {
  const parts = subjectParts(placement);
  const root = new TransformNode('coverage:subject', scene);

  const material = new StandardMaterial('coverage:material', scene);
  material.diffuseColor = Color3.White();
  material.specularColor = Color3.Black();
  // Enough self-light that a colour reads in a shadow, little enough that the shape still does.
  material.emissiveColor = new Color3(0.4, 0.4, 0.4);

  const meshes: Mesh[] = parts.map((part, index) => {
    const a = Vector3.FromArray(part.a);
    const b = Vector3.FromArray(part.b);
    const length = Vector3.Distance(a, b);
    const mesh =
      length < 1e-6
        ? MeshBuilder.CreateSphere(`coverage:${index}`, { diameter: part.radius * 2, segments: 8 }, scene)
        : MeshBuilder.CreateCapsule(
            `coverage:${index}`,
            {
              radius: part.radius,
              height: length + part.radius * 2,
              tessellation: TESSELLATION,
              capSubdivisions: CAP_SUBDIVISIONS,
              subdivisions: 1,
            },
            scene
          );
    mesh.position = Vector3.Center(a, b);
    // Turned by the node rather than the builder's `orientation`, which moves
    // the vertices but leaves every normal pointing as if the capsule stood up.
    if (length >= 1e-6) {
      // A capsule is symmetric, so turn it the short way: rotating Up to nearly
      // Down is where the quaternion loses precision.
      const axis = b.subtract(a).normalize();
      mesh.rotationQuaternion = Quaternion.FromUnitVectorsToRef(
        Vector3.Up(),
        axis.y < 0 ? axis.negate() : axis,
        new Quaternion()
      );
    }
    mesh.parent = root;
    mesh.material = material;
    mesh.isPickable = false;
    return mesh;
  });

  const offsets: number[] = [];
  const samples: Sample[] = [];
  meshes.forEach((mesh, part) => {
    offsets.push(samples.length);
    const world: Matrix = mesh.computeWorldMatrix(true);
    const positions = mesh.getVerticesData(VertexBuffer.PositionKind) ?? [];
    const normals = mesh.getVerticesData(VertexBuffer.NormalKind) ?? [];
    const indices = mesh.getIndices() ?? [];

    // A vertex stands for a third of every triangle it is a corner of.
    const weights = new Array<number>(positions.length / 3).fill(0);
    const corner = (i: number) => Vector3.FromArray(positions, i * 3);
    for (let i = 0; i < indices.length; i += 3) {
      const [p, q, r] = [indices[i], indices[i + 1], indices[i + 2]];
      const area = Vector3.Cross(corner(q).subtract(corner(p)), corner(r).subtract(corner(p))).length() / 2;
      weights[p] += area / 3;
      weights[q] += area / 3;
      weights[r] += area / 3;
    }

    for (let v = 0; v < positions.length / 3; v++) {
      const position = Vector3.TransformCoordinates(corner(v), world);
      const normal = Vector3.TransformNormal(Vector3.FromArray(normals, v * 3), world).normalize();
      samples.push({
        position: position.asArray() as unknown as Point,
        normal: normal.asArray() as unknown as Point,
        part,
        weight: weights[v],
      });
    }
  });

  return {
    parts,
    samples,

    paint: (colourOf) => {
      meshes.forEach((mesh, part) => {
        const start = offsets[part];
        const end = part + 1 < offsets.length ? offsets[part + 1] : samples.length;
        const colours = new Float32Array((end - start) * 4);
        for (let i = start; i < end; i++) {
          const [r, g, b] = colourOf(i);
          colours.set([r, g, b, 1], (i - start) * 4);
        }
        mesh.setVerticesData(VertexBuffer.ColorKind, colours, true);
      });
    },

    dispose: () => {
      for (const mesh of meshes) {
        mesh.dispose();
      }
      material.dispose();
      root.dispose();
    },
  };
};
